import { useEffect, useState } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Alert,
  Avatar,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  SelectChangeEvent,
  CircularProgress,
} from '@mui/material';
import { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import ResponsiveDialog from './ResponsiveDialog';
import { updateDoc, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { UserRole } from '../types/user';
import { colors } from '../theme/variables';
import { userPath } from '../utils/dbPaths';
import { ParentLinkService } from '../services/parentLinkService';

// Icons
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

interface OnboardingDialogProps {
  open: boolean;
  userId: string;
  userEmail: string | null;
  onComplete: () => void;
}

// Step identifiers used for dispatch logic — stable, never translated. The
// translated labels shown in the Stepper are derived from these via `t()`
// inside the component (module scope can't call `t`).
const STEP_KEYS = ['basicInfo', 'chooseRole', 'complete'] as const;
type StepKey = (typeof STEP_KEYS)[number];

const getRoleOptions = (
  t: TFunction
): { role: UserRole; label: string; description: string; icon: JSX.Element }[] => [
  {
    role: 'athlete',
    label: t('onboarding.roles.athlete.label'),
    description: t('onboarding.roles.athlete.description'),
    icon: <SportsBasketballIcon sx={{ fontSize: 48 }} />,
  },
  {
    role: 'coach',
    label: t('onboarding.roles.coach.label'),
    description: t('onboarding.roles.coach.description'),
    icon: <EmojiEventsIcon sx={{ fontSize: 48 }} />,
  },
  {
    role: 'parent',
    label: t('onboarding.roles.parent.label'),
    description: t('onboarding.roles.parent.description'),
    icon: <FamilyRestroomIcon sx={{ fontSize: 48 }} />,
  },
  {
    role: 'school_admin',
    label: t('onboarding.roles.schoolAdmin.label'),
    description: t('onboarding.roles.schoolAdmin.description'),
    icon: <AdminPanelSettingsIcon sx={{ fontSize: 48 }} />,
  },
  // COMMENTED OUT FOR FIRST RELEASE - Recruiting feature
  // {
  //   role: 'recruiter',
  //   label: 'Recruiter',
  //   description: 'Discover and connect with talented athletes',
  //   icon: <PersonSearchIcon sx={{ fontSize: 48 }} />,
  // },
];

export default function OnboardingDialog({ open, userId, userEmail, onComplete }: OnboardingDialogProps) {
  const { t } = useTranslation('auth');
  const roleOptions = getRoleOptions(t);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [displayName, setDisplayName] = useState('');
  const [location, setLocation] = useState('');
  const [bio, setBio] = useState('');
  // V1 Privacy: Account type removed - all users are org-bound
  const [selectedRole, setSelectedRole] = useState<UserRole>('athlete');

  // Role-specific state
  const [university, setUniversity] = useState('');
  const [position, setPosition] = useState('');
  const [coachingExperience, setCoachingExperience] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [sportsFocus, setSportsFocus] = useState<string[]>([]);
  // Parent-specific state
  const [parentInviteCode, setParentInviteCode] = useState('');

  // District-managed accounts (SSO/roster): identity and role come from the
  // SIS — no role picker, no invite codes. Parents are already auto-linked
  // to their children by the roster's guardian data.
  const [isManaged, setIsManaged] = useState(false);
  const [linkedChildrenNames, setLinkedChildrenNames] = useState<string[]>([]);

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, userPath(userId)));
        if (!snap.exists() || cancelled) return;
        const profile = snap.data();
        if (profile.account_type !== 'managed') return;
        setIsManaged(true);
        if (profile.display_name) setDisplayName(profile.display_name);
        if (profile.role) setSelectedRole(profile.role as UserRole);
        const childIds: string[] = (profile.linked_children_ids || []).slice(0, 5);
        if (childIds.length > 0) {
          const children = await Promise.all(
            childIds.map((id) => getDoc(doc(db, userPath(id))).catch(() => null))
          );
          if (!cancelled) {
            setLinkedChildrenNames(
              children
                .map((c) => (c?.exists() ? c.data()?.display_name : null))
                .filter(Boolean) as string[]
            );
          }
        }
      } catch {
        // Fall back to the standard flow
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, userId]);

  const activeStepKeys: StepKey[] = isManaged
    ? ['basicInfo', 'complete']
    : [...STEP_KEYS];
  const stepLabels: Record<StepKey, string> = {
    basicInfo: t('onboarding.steps.basicInfo'),
    chooseRole: t('onboarding.steps.chooseRole'),
    complete: t('onboarding.steps.complete'),
  };
  const activeSteps = activeStepKeys.map((key) => stepLabels[key]);

  const handleNext = () => {
    if (activeStep === 0) {
      if (!displayName.trim()) {
        setError(t('onboarding.errors.nameRequired'));
        return;
      }
    }
    setError(null);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => prev - 1);
  };

  const handleSportsFocusChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSportsFocus(typeof value === 'string' ? value.split(',') : value);
  };

  const handleComplete = async () => {
    setLoading(true);
    setError(null);

    try {
      const userRef = doc(db, userPath(userId));
      const metadata: any = {
        location: location || '',
        bio: bio || '',
      };

      // Add role-specific fields
      // COMMENTED OUT FOR FIRST RELEASE - Recruiting feature
      // if (selectedRole === 'recruiter') {
      //   metadata.university = university;
      //   metadata.position = position;
      //   metadata.sportsFocus = sportsFocus;
      // } else
      if (selectedRole === 'coach') {
        metadata.coachingExperience = coachingExperience;
        metadata.specialization = specialization;
      }

      // V1 Privacy: Default to public profile visibility within org (no cross-org access)
      // Managed accounts keep their SIS-provided name and roster-derived role.
      await updateDoc(userRef, {
        ...(isManaged ? {} : { display_name: displayName.trim(), role: selectedRole }),
        metadata,
        has_completed_onboarding: true,
        profile_visibility: 'public', // Default to public profile for org members
        updated_at: new Date(),
      });

      // If parent selected and provided an invite code, link them to the child
      // (self-service families only — district parents are roster-linked)
      if (!isManaged && selectedRole === 'parent' && parentInviteCode.trim()) {
        try {
          await ParentLinkService.acceptParentInvite(userId, parentInviteCode.trim());
        } catch (linkError: any) {          // Don't fail the onboarding - they can link later
          // Just show a warning in the UI
        }
      }

      onComplete();
    } catch (err) {      setError(t('onboarding.errors.completeFailed'));
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    // Managed accounts skip the role step, so dispatch on the step KEY (never
    // the translated label — that would break once the label is localized).
    switch (activeStepKeys[step]) {
      case 'basicInfo':
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('onboarding.basicInfo.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('onboarding.basicInfo.subtitle')}
            </Typography>

            <TextField
              fullWidth
              label={t('onboarding.basicInfo.nameLabel')}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              disabled={isManaged}
              helperText={isManaged ? t('onboarding.basicInfo.managedNameHelper') : undefined}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label={t('onboarding.basicInfo.locationLabel')}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder={t('onboarding.basicInfo.locationPlaceholder')}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label={t('onboarding.basicInfo.bioLabel')}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              multiline
              rows={3}
              placeholder={t('onboarding.basicInfo.bioPlaceholder')}
            />
          </Box>
        );

      // V1 Privacy: Account Type step removed - case 1 is now Choose Your Role
      case 'chooseRole':
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('onboarding.role.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('onboarding.role.subtitle')}
            </Typography>

            <Grid container spacing={2}>
              {roleOptions.map((option) => (
                <Grid item xs={12} key={option.role}>
                  <Card
                    sx={{
                      border: selectedRole === option.role ? `2px solid ${colors.primary.main}` : '1px solid',
                      borderColor: selectedRole === option.role ? colors.primary.main : 'divider',
                      background:
                        selectedRole === option.role
                          ? `linear-gradient(135deg, ${colors.primary.alpha[10]} 0%, transparent 100%)`
                          : 'transparent',
                    }}
                  >
                    <CardActionArea onClick={() => setSelectedRole(option.role)}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ color: selectedRole === option.role ? 'primary.main' : 'text.secondary' }}>
                            {option.icon}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" gutterBottom>
                              {option.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {option.description}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Role-specific fields */}
            {/* COMMENTED OUT FOR FIRST RELEASE - Recruiting feature */}
            {/* {selectedRole === 'recruiter' && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Recruiter Information
                </Typography>
                <TextField
                  fullWidth
                  label="University/Organization"
                  value={university}
                  onChange={(e) => setUniversity(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                  placeholder="e.g., UCLA"
                />
                <TextField
                  fullWidth
                  label="Position/Title"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                  placeholder="e.g., Assistant Recruiting Coordinator"
                />
                <FormControl fullWidth>
                  <InputLabel>Sports You Recruit For</InputLabel>
                  <Select
                    multiple
                    value={sportsFocus}
                    onChange={handleSportsFocusChange}
                    input={<OutlinedInput label="Sports You Recruit For" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {SPORTS_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s.toLowerCase()}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )} */}

            {selectedRole === 'coach' && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('onboarding.role.coachSectionTitle')}
                </Typography>
                <TextField
                  fullWidth
                  label={t('onboarding.role.experienceLabel')}
                  value={coachingExperience}
                  onChange={(e) => setCoachingExperience(e.target.value)}
                  sx={{ mb: 2 }}
                  placeholder={t('onboarding.role.experiencePlaceholder')}
                />
                <TextField
                  fullWidth
                  label={t('onboarding.role.specializationLabel')}
                  value={specialization}
                  onChange={(e) => setSpecialization(e.target.value)}
                  placeholder={t('onboarding.role.specializationPlaceholder')}
                />
              </Box>
            )}

            {selectedRole === 'parent' && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('onboarding.role.parentSectionTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('onboarding.role.parentInviteDescription')}
                </Typography>
                <TextField
                  fullWidth
                  label={t('onboarding.role.parentInviteLabel')}
                  value={parentInviteCode}
                  onChange={(e) => setParentInviteCode(e.target.value.toUpperCase())}
                  placeholder={t('onboarding.role.parentInvitePlaceholder')}
                  inputProps={{ maxLength: 8, style: { textTransform: 'uppercase' } }}
                />
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t('onboarding.role.parentInviteHint')}
                </Alert>
              </Box>
            )}
          </Box>
        );

      // V1 Privacy: Renumbered - was case 3
      case 'complete':
        return (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 2,
                background: colors.gradients.primary,
                fontSize: '2rem',
              }}
            >
              {displayName?.[0] || '?'}
            </Avatar>
            <Typography variant="h5" gutterBottom>
              {t('onboarding.complete.welcome', { name: displayName })}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {/* <Trans>, not two half-sentences: Spanish and English put the
                  role chip in different places in the sentence. */}
              <Trans
                t={t}
                i18nKey="onboarding.complete.summary"
                components={{
                  role: <Chip label={selectedRole} color="primary" size="small" />,
                }}
              />
            </Typography>

            {/* V1 Privacy: recruiter role alert removed */}

            {isManaged && selectedRole === 'parent' && (
              <Alert severity="success" sx={{ mt: 2, textAlign: 'left' }}>
                {linkedChildrenNames.length > 0
                  ? t('onboarding.complete.managedParentLinked', {
                      children: linkedChildrenNames.join(', '),
                    })
                  : t('onboarding.complete.managedParentPending')}
              </Alert>
            )}

            {!isManaged && selectedRole === 'parent' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {parentInviteCode.trim()
                  ? t('onboarding.complete.selfParentLinked')
                  : t('onboarding.complete.selfParentPending')}
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <ResponsiveDialog open={open} maxWidth="md" fullWidth disableEscapeKeyDown>
      <DialogTitle>
        <Typography variant="h5" fontWeight={700}>
          {t('onboarding.dialogTitle')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {activeSteps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleBack} disabled={activeStep === 0 || loading}>
          {t('common:actions.back')}
        </Button>
        <Box sx={{ flex: 1 }} />
        {activeStep < activeSteps.length - 1 ? (
          <Button variant="contained" onClick={handleNext} disabled={loading}>
            {t('common:actions.next')}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleComplete} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : t('onboarding.actions.getStarted')}
          </Button>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
}
